<template lang="pug">
header(
  ref="headerRef"
  :class="{'show-mobilemenu': overlay === 'mobilemenu'}"
  @click="menuClick"
)
  .mobile-backdrop(:class="overlay === 'mobilemenu' && 'show'" @click="uiStore.hideOverlay()")
  DynamicBlock(name="above-header")
  header-top
  header-main
  header-links
</template>
<script setup lang="ts">
import { allowScroll, preventScroll } from 'sisu/js/body-scroll'
import { computed, defineAsyncComponent, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue'
import { useResizeObserver } from '@vueuse/core'
import { getTargetBackend } from 'Src/js/helpers/environment'
import { useRoute, useRouter } from 'vue-router'
import { useMQ } from 'sisu/js/mq'
import { useUIStore } from '@/js/stores/uiStore'
import { useRogue } from '@/js/services/Rogue'
import DynamicBlock from '@/js/components/dynamic-block.vue'
import HeaderMain from '@/js/components/header/main.vue'
import HeaderLinks from '@/js/components/header/links.vue'

// Matches links.vue's `.dropdown { transition: left 300ms; }`
const MOBILE_MENU_CLOSE_TRANSITION_MS = 300

const HeaderTop = defineAsyncComponent({
  loader: () => import(/* webpackChunkName: "ssr-header-static"*/ '@/js/components/header/top.vue'),
})

const uiStore = useUIStore()
const route = useRoute()
const router = useRouter()
const rogue = useRogue()
const mq = useMQ()
const headerRef = useTemplateRef<HTMLElement>('headerRef')

let menuResetTimeout: ReturnType<typeof setTimeout> | undefined

const active = ref<HTMLElement | null>(null)

const overlay = computed(() => uiStore.overlay)

// Expose the header's real rendered height so pages can size loading states
// against the space actually left below it.
useResizeObserver(headerRef, ([entry]) => {
  document.documentElement.style.setProperty('--header-height', `${entry.contentRect.height}px`)
})

function menuReset() {
  if ((mq.value === 'mobile' || mq.value === 'tablet') && !rogue.ssrContext) {
    // Reset the menu items
    const menu = document.querySelector('.shop-mega')
    const items = menu?.querySelectorAll('.menu-item') || []
    items.forEach(item => item.classList.remove('hide', 'active', 'active-child'))
  }
}

function menuClick(e: Event) {
  let tgt = e.target as HTMLElement

  if (tgt.classList?.contains('change-country')) {
    if (overlay.value === 'country-select-v2') {
      uiStore.hideOverlay()
    } else {
      uiStore.showOverlay('country-select-v2', rogue)
    }

    return
  }
  let link = tgt.closest('a')

  if (link?.getAttribute('data-locale')) {
    e.preventDefault()
    e.stopPropagation()

    const locale = link.getAttribute('data-locale')
    const hostname = `${getTargetBackend(rogue.env)}.${link.getAttribute('data-hostname')}`
    if (locale && hostname) {
      let localeIdx
      let path = '/'
      let params: string[] = []
      const { search, protocol, host, pathname } = window.location

      if (hostname === host) {
        path = pathname
        params = search.slice(1).split('&').map((p, idx) => {
          if (p.startsWith('locale=')) {
            const temp = p.split('=')
            temp[1] = locale
            localeIdx = idx

            return temp.join('=')
          }

          return p
        })
      }

      if (localeIdx === undefined) {
        params.push(`locale=${locale}`)
      }
      window.location.href = `${protocol}//${hostname}${path}?${params.join('&')}`
    }

    return
  }

  if (['mobile', 'tablet'].indexOf(mq.value) === -1) {
    return
  }

  if (tgt.classList.contains('tab-title')) {
    if (tgt.innerText === rogue.__('The Index')) {
      router.push('/theindex')
    }

    return
  }

  if (tgt.classList.contains('hamburger')) {
    uiStore.showOverlay('mobilemenu', rogue)

    return
  }

  if (tgt.classList.contains('arrow') || tgt.classList.contains('menu-link')) {
    tgt = tgt.parentElement!.closest('.menu-item')!
  }
  const menu = tgt.closest('.mega')

  if ((mq.value === 'mobile' || mq.value === 'tablet') && tgt.classList.contains('menu-item')) {
    e.preventDefault()
    e.stopPropagation()

    if (!tgt.querySelector('.panel')) {
      link = tgt.querySelector('a')
      if (link) {
        router.push(link!.getAttribute('href')!)
        uiStore.hideOverlay()
      }

      return
    }

    if (tgt === active.value) {
      tgt = active.value.parentElement!.closest('.menu-item')!
    }
    active.value = tgt

    const items = menu?.querySelectorAll('.menu-item') || []

    items.forEach(item => {
      const contains = item.contains(tgt)
      item.classList.toggle('active', item === tgt)
      item.classList.toggle('hide', tgt && !contains)
      item.classList.toggle('active-child', item !== tgt && contains)
    })
  }
}

watch(mq, newVal => {
  if (overlay.value === 'mobilemenu' && ['mobile', 'tablet'].indexOf(newVal) === -1) {
    uiStore.hideOverlay()
  }
})

watch(overlay, (newVal, oldVal) => {
  if (newVal === 'mobilemenu' && !oldVal) {
    preventScroll()
  } else if (oldVal === 'mobilemenu' && !newVal) {
    allowScroll()
  }
})

watch(() => route.path, () => {
  if (overlay.value === 'mobilemenu') {
    uiStore.hideOverlay()

    // Resetting menu-item classes immediately makes every top-level item reappear while the
    // panel is still visibly sliding off screen, so wait for that transition to finish first.
    clearTimeout(menuResetTimeout)
    menuResetTimeout = setTimeout(() => menuReset(), MOBILE_MENU_CLOSE_TRANSITION_MS)
  } else {
    menuReset()
  }

  const hovers = headerRef.value?.querySelectorAll('.has-dropdown') || []

  for (let i = 0; i < hovers.length; i += 1) {
    hovers[i].classList.add('force-hide')
  }
  setTimeout(() => {
    for (let i = 0; i < hovers.length; i += 1) {
      hovers[i].classList.remove('force-hide')
    }
  }, 50)
})

onMounted(() => {
  nextTick(() => {
    const dealSlides = headerRef.value?.querySelector('.deal-slides')

    if (dealSlides && dealSlides.children.length > 1) {
      setInterval(() => {
        // Dont transition if the deal slides element is hovered
        if (dealSlides.matches(':hover')) {
          return
        }
        const activeSlide = dealSlides.querySelector('.show')
        activeSlide?.classList.remove('show')
        const next = activeSlide?.nextElementSibling || dealSlides.children[0]
        next.classList.add('show')
      }, 4000)
    }
  })
})

onUnmounted(() => {
  allowScroll()
  clearTimeout(menuResetTimeout)
})
</script>
<style lang="scss">
@include not-desktop() {
  .dropdown.shop-menu {
    display: flex;
  }
}
</style>

<style lang="scss" scoped>

.mobile-backdrop {
  opacity: 0;
  pointer-events: none;
  background-color: rgba(0, 0, 0, 0.75);
  transition: opacity 300ms;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 100;

  &.show {
    opacity: 1;
    pointer-events: all;
  }
}
</style>
